import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../context/AuthContext';
import { describeGoogleSignInError } from '../utils/googleSignInErrors';

/**
 * The landing point of the Google redirect: adopts the token the server issued,
 * then continues to wherever the user was heading.
 */
const GoogleCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  // The token is single use, so a second run under React's strict double-invoke
  // would report a failure for a sign-in that already succeeded.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const failure = searchParams.get('error');
    if (failure) {
      setError(describeGoogleSignInError(failure) || describeGoogleSignInError('google_failed'));
      return;
    }

    const token = searchParams.get('token');
    if (!token) {
      setError('No sign-in token arrived from the server. Please try again.');
      return;
    }

    const target = searchParams.get('redirect');
    // Only an in-app path is followed, so a crafted link cannot bounce a
    // freshly signed-in user off to another site.
    const safeTarget = target && target.startsWith('/') && !target.startsWith('//')
      ? target
      : '/dashboard';

    loginWithToken(token).then(signedIn => {
      if (signedIn) navigate(safeTarget, { replace: true });
      else setError('That sign-in token was not accepted. Please try again.');
    });
  }, [searchParams, navigate, loginWithToken]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        {error ? (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Sign-in failed</Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert>
            <Button fullWidth variant="contained" onClick={() => navigate('/login', { replace: true })}>
              Back to sign in
            </Button>
          </>
        ) : (
          <>
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>Signing you in</Typography>
            <Typography variant="body2" color="text.secondary">
              Finishing up with Google.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default GoogleCallback;
